// frontend/src/pages/DashboardPage.tsx - NEW FUNCTION - Fixed version without console errors
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';

// UI Components - correct import paths
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';

// Import the correct dashboard hooks from dedicated dashboard hook file
import { 
  useDashboard, 
  useDashboardMetrics, 
  useDashboardActivity,
  useDashboardActionSummary
} from '@/hooks/useDashboard';

// Type definitions for the dashboard data structures
interface DashboardMetric {
  label: string;
  value: number | string;
  icon: string;
  color: string;
  description: string;
}

interface ActivityItem {
  type: string;
  id: string;
  title: string;
  description: string;
  timestamp: string;
  metadata?: any;
}

interface DashboardCounts {
  emails?: number;
  correspondences?: number;
  responses?: number;
  projects?: number;
  pendingApprovals?: number;
}

interface DashboardSummary {
  user?: {
    id: string;
    username: string;
    role: string;
    organizationName: string;
  };
  counts?: DashboardCounts;
  recentActivity?: any[];
  upcomingTasks?: any[];
  storageUsage?: {
    used: number;
    limit: number;
    percentage: number;
  };
  notifications?: {
    unread: number;
    urgent: number;
  };
}

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { addNotification } = useAppStore();
  
  // State for manual refresh tracking
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [refreshing, setRefreshing] = useState(false);
  
  // Check user permissions safely
  const hasMetricsPermission = user?.role === 'admin' || user?.role === 'implementor';
  const isImplementor = user?.role === 'implementor';
  
  // Use the dashboard hooks
  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard
  } = useDashboard();
  
  // Load additional metrics only if user has permission
  const {
    data: metricsData,
    loading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics
  } = useDashboardMetrics(hasMetricsPermission ? { period: 'week' } : undefined);

  // What actually needs the user's attention. Rendered ahead of the raw totals
  // for admins and contributors; implementors keep the system view.
  const { data: actionSummary } = useDashboardActionSummary({ immediate: !isImplementor } as any);
  
  // Load recent activity
  const {
    data: activityData,
    loading: activityLoading,
    error: activityError,
    refetch: refetchActivity
  } = useDashboardActivity(hasMetricsPermission ? { limit: 5 } : undefined);
  
  // Computed loading and error states
  const isLoading = dashboardLoading || (hasMetricsPermission && (metricsLoading || activityLoading));
  const hasError = dashboardError || metricsError || activityError;
  
  // Manual refresh function
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // Refetch all data safely
      const promises = [refetchDashboard()];
      
      if (hasMetricsPermission && refetchMetrics) {
        promises.push(refetchMetrics());
      }
      
      if (hasMetricsPermission && refetchActivity) {
        promises.push(refetchActivity());
      }
      
      await Promise.all(promises);
      
      setLastRefresh(new Date());
      
      if (addNotification) {
        addNotification({
          type: 'success',
          title: 'Dashboard Updated',
          message: 'Dashboard data has been refreshed successfully.'
        });
      }
    } catch (error: any) {
      console.error('Dashboard refresh failed:', error);
      
      if (addNotification) {
        addNotification({
          type: 'error',
          title: 'Refresh Failed',
          message: error?.message || 'Failed to refresh dashboard data.'
        });
      }
    } finally {
      setRefreshing(false);
    }
  }, [refetchDashboard, refetchMetrics, refetchActivity, hasMetricsPermission, addNotification]);
  
  // Generate metrics based on data and user role
  const getMetrics = useCallback((): DashboardMetric[] => {
    // Type the dashboard data safely
    const typedDashboardData = dashboardData as DashboardSummary | undefined;
    const counts = typedDashboardData?.counts || {};
    
    if (isImplementor) {
      // For implementors, show system-wide metrics
      // Use basic counts and supplement with analytics data if available
      return [
        {
          label: 'Organizations',
          value: (metricsData as any)?.totalOrganizations || (metricsData as any)?.numOrganizations || 0,
          icon: '🏢',
          color: 'text-blue-600',
          description: 'Total organizations in system'
        },
        {
          label: 'Users',
          value: (metricsData as any)?.totalUsers || (metricsData as any)?.numUsers || 0,
          icon: '👥',
          color: 'text-green-600',
          description: 'Active users across all organizations'
        },
        {
          label: 'Projects',
          value: counts.projects || (metricsData as any)?.totalProjects || 0,
          icon: '📋',
          color: 'text-purple-600',
          description: 'Total projects in system'
        },
        {
          label: 'Groups',
          value: (metricsData as any)?.totalGroups || (metricsData as any)?.numGroups || 0,
          icon: '👥',
          color: 'text-orange-600',
          description: 'User groups across organizations'
        },
        {
          label: 'System Health',
          value: 'Good',
          icon: '✅',
          color: 'text-green-600',
          description: 'Overall system status'
        }
      ];
    } else {
      // For regular users, show their organization's metrics
      return [
        {
          label: 'Emails',
          value: counts.emails || 0,
          icon: '📧',
          color: 'text-blue-600',
          description: 'Total emails in system'
        },
        {
          label: 'Correspondences',
          value: counts.correspondences || 0,
          icon: '💬',
          color: 'text-green-600',
          description: 'Active correspondences'
        },
        {
          label: 'Responses',
          value: counts.responses || 0,
          icon: '✏️',
          color: 'text-purple-600',
          description: 'Draft and sent responses'
        },
        {
          label: 'Projects',
          value: counts.projects || 0,
          icon: '📂',
          color: 'text-orange-600',
          description: 'Active projects'
        },
        {
          label: 'Pending',
          value: counts.pendingApprovals || 0,
          icon: '⏳',
          color: 'text-red-600',
          description: 'Items requiring attention'
        }
      ];
    }
  }, [dashboardData, metricsData, isImplementor]);
  
  // Generate activity items safely
  const getActivityItems = useCallback((): ActivityItem[] => {
    // Try to get activity from multiple sources
    const typedDashboardData = dashboardData as DashboardSummary | undefined;
    const dashboardActivity = typedDashboardData?.recentActivity || [];
    const hookActivity = Array.isArray(activityData?.data) ? activityData.data : [];
    
    // Combine and normalize activity items
    const allActivities = [...dashboardActivity, ...hookActivity];
    
    if (allActivities.length > 0) {
      return allActivities.map((item: any, index: number) => ({
        type: item.type || 'activity',
        id: item.id || `activity-${index}`,
        title: item.action || item.title || 'Activity',
        description: item.description || 'No details available',
        timestamp: item.timestamp || new Date().toISOString(),
        metadata: item
      })).slice(0, 5); // Limit to 5 most recent items
    }
    
    return [];
  }, [dashboardData, activityData]);
  
  // Handle quick actions
  const handleQuickAction = useCallback((action: string) => {
    try {
      switch (action) {
        case 'create-correspondence':
          navigate('/correspondences/new');
          break;
        case 'view-emails':
          navigate('/emails');
          break;
        case 'manage-users':
          navigate('/users');
          break;
        case 'view-projects':
          navigate('/projects');
          break;
        case 'manage-organizations':
          navigate('/organizations');
          break;
        case 'system-settings':
          navigate('/settings');
          break;
        case 'manage-documents':
          navigate('/static-documents');
          break;
        default:
      }
    } catch (error) {
      console.error('Navigation failed:', error);
    }
  }, [navigate]);
  
  // Show loading state
  if (isLoading && !dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
            <span className="ml-3 text-lg text-gray-600">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }
  
  const metrics = getMetrics();
  const activityItems = getActivityItems();
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isImplementor ? 'System Dashboard' : 'Dashboard'}
            </h1>
            <p className="text-gray-600 mt-1">
              Welcome back, {user?.username || 'User'}
              {isImplementor && ' - System Administrator'}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              variant="secondary"
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center"
            >
              {refreshing ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : (
                <span className="mr-2">🔄</span>
              )}
              Refresh
            </Button>
            <span className="text-sm text-gray-500">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
          </div>
        </div>
        
        {/* Error Alert */}
        {hasError && (
          <ErrorAlert
            message={
              dashboardError || 
              metricsError || 
              activityError || 
              'Failed to load dashboard data'
            }
            className="mb-6"
            onClose={() => {}} // Error will clear on next successful load
          />
        )}
        
        {/* Implementor Notice */}
        {isImplementor && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <span className="text-purple-600 mr-2">🛡️</span>
              <p className="text-purple-800">
                System Administrator Dashboard - You have access to system-wide data and settings.
              </p>
            </div>
          </div>
        )}
        
        {/* Needs your attention — shown before the totals, because a count of
            400 emails tells a user nothing about what to do next. */}
        {!isImplementor && actionSummary && (() => {
          const pa = (actionSummary as any).pendingActions || {};
          const awaiting = pa.responsesAwaitingAction || {};
          const forResp = pa.correspondencesForResponse || {};
          const queue = pa.unclassifiedEmails || {};
          const since = (actionSummary as any).sinceLastLogin || {};
          const isAdmin = user?.role === 'admin';
          const cardCls = 'bg-white rounded-lg shadow-sm border border-gray-200 p-5';
          const numCls = 'text-2xl font-bold text-gray-900';
          const lblCls = 'text-xs font-semibold text-gray-500 uppercase tracking-wide';
          return (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Needs your attention</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <button type="button" onClick={() => navigate('/responses')} className={cardCls + ' text-left hover:border-blue-300'}>
                  <div className={lblCls}>Responses waiting on you</div>
                  <div className={numCls}>{awaiting.total ?? 0}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {awaiting.urgent ? `${awaiting.urgent} urgent · ` : ''}
                    {Object.entries(awaiting.byRole || {}).filter(([, n]) => (n as number) > 0).map(([r, n]) => `${n} as ${r}`).join(', ') || 'nothing pending'}
                  </div>
                </button>
                <button type="button" onClick={() => navigate('/correspondences')} className={cardCls + ' text-left hover:border-blue-300'}>
                  <div className={lblCls}>Letters awaiting a reply</div>
                  <div className={numCls}>{forResp.total ?? 0}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {forResp.oldest ? `oldest: ${new Date(forResp.oldest).toLocaleDateString()}` : 'in your projects'}
                  </div>
                </button>
                <button type="button" onClick={() => navigate('/emails')} className={cardCls + ' text-left hover:border-blue-300'}>
                  <div className={lblCls}>{isAdmin ? 'Emails not yet filed' : 'Your unfiled emails'}</div>
                  <div className={numCls}>{queue.total ?? 0}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {queue.awaitingClarification ? `${queue.awaitingClarification} need a project chosen · ` : ''}
                    {queue.failed ? `${queue.failed} failed · ` : ''}
                    {(!queue.awaitingClarification && !queue.failed) ? 'not yet converted to correspondence' : ''}
                  </div>
                </button>
                <div className={cardCls}>
                  <div className={lblCls}>Since your last login</div>
                  <div className={numCls}>{(since.newEmails ?? 0) + (since.newCorrespondences ?? 0)}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {since.newEmails ?? 0} emails · {since.newCorrespondences ?? 0} correspondences · {since.responseActivity ?? 0} response updates
                  </div>
                </div>
              </div>
              {Array.isArray(awaiting.responses) && awaiting.responses.length > 0 && (
                <div className="mt-3 bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
                  {awaiting.responses.slice(0, 5).map((r: any) => (
                    <button key={r.id} type="button" onClick={() => navigate(`/responses/${r.id}`)}
                      className="w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-gray-50">
                      <span className="text-sm text-gray-900 truncate">
                        {r.referenceNumber ? <span className="font-mono text-xs text-gray-500 mr-2">{r.referenceNumber}</span> : null}
                        {r.subject || 'Response'}
                      </span>
                      <span className={'text-xs ml-3 flex-shrink-0 ' + (r.isOverdue ? 'text-red-600 font-semibold' : 'text-gray-500')}>
                        {r.stage}{r.role ? ` · ${r.role}` : ''}{r.isOverdue ? ' · overdue' : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {metrics.map((metric, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 truncate">
                    {metric.label}
                  </p>
                  <p className={`text-2xl font-semibold ${metric.color}`}>
                    {typeof metric.value === 'number' 
                      ? metric.value.toLocaleString()
                      : metric.value
                    }
                  </p>
                </div>
                <div className="text-2xl">{metric.icon}</div>
              </div>
              <p className="text-xs text-gray-500 mt-2 truncate">
                {metric.description}
              </p>
            </div>
          ))}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {isImplementor ? (
                    // Implementor actions
                    <>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleQuickAction('manage-organizations')}
                        className="w-full justify-start"
                      >
                        🏢 Manage Organizations
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleQuickAction('manage-users')}
                        className="w-full justify-start"
                      >
                        👥 Manage Users
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleQuickAction('system-settings')}
                        className="w-full justify-start"
                      >
                        ⚙️ System Settings
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleQuickAction('view-projects')}
                        className="w-full justify-start"
                      >
                        📋 View Projects
                      </Button>
                    </>
                  ) : (
                    // Regular user actions
                    <>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleQuickAction('create-correspondence')}
                        className="w-full justify-start"
                      >
                        ✉️ New Correspondence
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleQuickAction('view-emails')}
                        className="w-full justify-start"
                      >
                        📧 View Emails
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleQuickAction('view-projects')}
                        className="w-full justify-start"
                      >
                        📂 Manage Projects
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleQuickAction('manage-documents')}
                        className="w-full justify-start"
                      >
                        📄 Static Documents
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
              </div>
              <div className="p-6">
                {activityItems.length > 0 ? (
                  <div className="space-y-4">
                    {activityItems.map((activity, index) => (
                      <div key={activity.id || index} className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        </div>
                        <div className="flex-grow">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.title}
                          </p>
                          <p className="text-sm text-gray-600">
                            {activity.description}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(activity.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No recent activity</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Activity will appear here as you use the system
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Storage Usage (if available) */}
        {dashboardData && (dashboardData as DashboardSummary).storageUsage && (
          <div className="mt-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Storage Usage</h3>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${(dashboardData as DashboardSummary).storageUsage?.percentage || 0}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-sm text-gray-600 mt-2">
                <span>
                  {((dashboardData as DashboardSummary).storageUsage?.used || 0).toLocaleString()} MB used
                </span>
                <span>
                  {((dashboardData as DashboardSummary).storageUsage?.limit || 0).toLocaleString()} MB total
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};